import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Colors } from "../../utils/Colors"

const ADMISSION_API = "http://127.0.0.1:8000/api/patients/admissions/?format=json";
const FLUID_THERAPY_API = "http://127.0.0.1:8000/api/medcharts/fluid-therapy/?format=json";

const fluidOptions = [
  { value: "NS", label: "Normal Saline" },
  { value: "RL", label: "Ringer Lactate" },
  { value: "DNS", label: "Dextrose Normal Saline" },
  { value: "D5", label: "5% Dextrose" },
  { value: "D10", label: "10% Dextrose" },
  { value: "OTHER", label: "Other" },
];

const qtyOptions = [
  { value: 100, label: "100 ml" },
  { value: 200, label: "200 ml" },
  { value: 300, label: "300 ml" },
  { value: 400, label: "400 ml" },
  { value: 500, label: "500 ml" },
  { value: 600, label: "600 ml" },
  { value: 700, label: "700 ml" },
  { value: 800, label: "800 ml" },
  { value: 900, label: "900 ml" },
  { value: 1000, label: "1000 ml" },
];

const inputStyle = {
  width: "100%",
  padding: "12px 14px",
  borderRadius: "12px",
  border: `1px solid ${Colors.border.input}`,
  outline: "none",
  fontSize: "14px",
  background: Colors.bg.paper,
  boxSizing: "border-box",
  color: Colors.text.body,
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontWeight: 700,
  color: Colors.text.title,
  fontSize: "14px",
};

const readOnlyStyle = {
  ...inputStyle,
  background: Colors.bg.hover,
  color: Colors.text.muted,
};

const sectionCardStyle = {
  background: Colors.bg.paper,
  border: `1px solid ${Colors.border.light}`,
  borderRadius: "18px",
  padding: "20px",
  boxShadow: "0 4px 14px rgba(11,60,63,0.05)",
};

export default function FluidTherapyForm() {
  const [admissions, setAdmissions] = useState([]);
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const [form, setForm] = useState({
    admission: "",
    admission_number: "",
    patient_uhid: "",
    patient_name: "",
    doctor_name: "",
    fluid: "",
    qty_ml: "",
    speed_ml_hr: "",
    frequency: "",
    duration: "",
  });

  useEffect(() => {
    fetchAdmissions();
  }, []);

  const fetchAdmissions = async () => {
    try {
      setLoadingAdmissions(true);
      setErrorMsg("");

      const res = await axios.get(ADMISSION_API);

      const data = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data?.results)
        ? res.data.results
        : [];

      setAdmissions(data);
    } catch (error) {
      console.error("Admissions fetch error:", error);
      setErrorMsg("Unable to load admissions.");
    } finally {
      setLoadingAdmissions(false);
    }
  };

  const selectedAdmission = useMemo(() => {
    return admissions.find(
      (item) => String(item.admission_id) === String(form.admission)
    );
  }, [admissions, form.admission]);

  useEffect(() => {
    if (!selectedAdmission) {
      setForm((prev) => ({
        ...prev,
        admission_number: "",
        patient_uhid: "",
        patient_name: "",
        doctor_name: "",
      }));
      return;
    }

    setForm((prev) => ({
      ...prev,
      admission_number:
        selectedAdmission.admission_number ||
        selectedAdmission.admission_no ||
        selectedAdmission.ip_number ||
        selectedAdmission.ip_no ||
        "",

      patient_uhid:
        selectedAdmission.patient_uhid ||
        selectedAdmission.patient_uhid_out ||
        selectedAdmission.uhid ||
        "",

      patient_name:
        selectedAdmission.patient_name ||
        selectedAdmission.name ||
        selectedAdmission.full_name ||
        "",

      doctor_name:
        selectedAdmission.treating_doctor ||
        selectedAdmission.attending_doctor ||
        selectedAdmission.consultant ||
        selectedAdmission.doctor_name ||
        "",
    }));
  }, [selectedAdmission]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setErrorMsg("");
    setSuccessMsg("");
    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const resetForm = () => {
    setForm({
      admission: "",
      admission_number: "",
      patient_uhid: "",
      patient_name: "",
      doctor_name: "",
      fluid: "",
      qty_ml: "",
      speed_ml_hr: "",
      frequency: "",
      duration: "",
    });
  };

  const validateForm = () => {
    if (!form.admission) return "Please select admission.";
    if (!form.fluid) return "Please select fluid.";
    if (!form.qty_ml) return "Please select quantity.";
    if (!form.speed_ml_hr) return "Please enter speed.";
    if (Number(form.speed_ml_hr) < 1) return "Speed must be at least 1 ml/hr.";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationError = validateForm();
    if (validationError) {
      setErrorMsg(validationError);
      return;
    }

    try {
      setSaving(true);
      setErrorMsg("");
      setSuccessMsg("");

      const payload = {
        admission: form.admission,
        fluid: form.fluid,
        qty_ml: Number(form.qty_ml),
        speed_ml_hr: Number(form.speed_ml_hr),
        frequency: form.frequency,
        duration: form.duration,
      };

      const response = await axios.post(FLUID_THERAPY_API, payload, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("Saved:", response.data);
      setSuccessMsg("Fluid therapy saved successfully.");
      resetForm();
    } catch (error) {
      console.error("Save error:", error);

      if (error.response?.data) {
        setErrorMsg(
          typeof error.response.data === "string"
            ? error.response.data
            : JSON.stringify(error.response.data)
        );
      } else {
        setErrorMsg("Failed to save fluid therapy.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        background: Colors.bg.app,
        padding: "32px 18px",
      }}
    >
      <div
        style={{
          maxWidth: "1100px",
          margin: "0 auto",
          background: Colors.bg.paper,
          borderRadius: "24px",
          boxShadow: Colors.shadow.brand,
          overflow: "hidden",
          border: `1px solid ${Colors.border.light}`,
        }}
      >
        <div
          style={{
            padding: "26px 30px 18px",
            background: Colors.bg.paper,
            borderBottom: `1px solid ${Colors.border.light}`,
            position: "relative",
          }}
        >
          <h2
            style={{
              margin: 0,
              fontSize: "28px",
              fontWeight: 800,
              color: Colors.text.title,
              letterSpacing: "0.2px",
            }}
          >
            Add Fluid Therapy
          </h2>

          <p
            style={{
              margin: "8px 0 0",
              color: Colors.text.muted,
              fontSize: "14px",
              lineHeight: 1.5,
            }}
          >
            Select admission and verify auto-filled patient details before saving therapy.
          </p>

          <div
            style={{
              marginTop: "16px",
              height: "6px",
              width: "140px",
              borderRadius: "999px",
              background: Colors.brand.accentBar,
            }}
          />
        </div>

        <form onSubmit={handleSubmit} style={{ padding: "28px" }}>
          {errorMsg && (
            <div
              style={{
                marginBottom: "18px",
                padding: "13px 14px",
                background: "#fff4f4",
                border: `1px solid ${Colors.status.danger}`,
                color: Colors.status.danger,
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {errorMsg}
            </div>
          )}

          {successMsg && (
            <div
              style={{
                marginBottom: "18px",
                padding: "13px 14px",
                background: "#f0fff4",
                border: `1px solid ${Colors.status.success}`,
                color: Colors.status.success,
                borderRadius: "12px",
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {successMsg}
            </div>
          )}

          <div style={{ ...sectionCardStyle, marginBottom: "22px" }}>
            <div style={{ marginBottom: "18px" }}>
              <h3
                style={{
                  margin: 0,
                  color: Colors.text.header,
                  fontSize: "18px",
                  fontWeight: 800,
                }}
              >
                Admission Selection
              </h3>
              <p
                style={{
                  margin: "6px 0 0",
                  color: Colors.text.sub,
                  fontSize: "13px",
                }}
              >
                Choose an admission to auto-fill patient information.
              </p>
            </div>

            <div>
              <label style={labelStyle}>Admission</label>
              <select
                name="admission"
                value={form.admission}
                onChange={handleChange}
                style={inputStyle}
                disabled={loadingAdmissions}
              >
                <option value="">
                  {loadingAdmissions ? "Loading admissions..." : "Select admission"}
                </option>

                {admissions.map((item) => (
                  <option key={item.admission_id} value={item.admission_id}>
                    {item.admission_no} - {item.patient_name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ ...sectionCardStyle, marginBottom: "22px" }}>
            <div style={{ marginBottom: "18px" }}>
              <h3
                style={{
                  margin: 0,
                  color: Colors.text.header,
                  fontSize: "18px",
                  fontWeight: 800,
                }}
              >
                Patient Details
              </h3>
              <p
                style={{
                  margin: "6px 0 0",
                  color: Colors.text.sub,
                  fontSize: "13px",
                }}
              >
                These details are auto-filled from the selected admission.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <label style={labelStyle}>Admission Number</label>
                <input
                  type="text"
                  value={form.admission_number}
                  readOnly
                  style={readOnlyStyle}
                  placeholder="Auto filled"
                />
              </div>

              <div>
                <label style={labelStyle}>Patient UHID</label>
                <input
                  type="text"
                  value={form.patient_uhid}
                  readOnly
                  style={readOnlyStyle}
                  placeholder="Auto filled"
                />
              </div>

              <div>
                <label style={labelStyle}>Patient Name</label>
                <input
                  type="text"
                  value={form.patient_name}
                  readOnly
                  style={readOnlyStyle}
                  placeholder="Auto filled"
                />
              </div>

              <div>
                <label style={labelStyle}>Doctor Name</label>
                <input
                  type="text"
                  value={form.doctor_name}
                  readOnly
                  style={readOnlyStyle}
                  placeholder="Auto filled"
                />
              </div>
            </div>
          </div>

          <div style={sectionCardStyle}>
            <div style={{ marginBottom: "18px" }}>
              <h3
                style={{
                  margin: 0,
                  color: Colors.text.header,
                  fontSize: "18px",
                  fontWeight: 800,
                }}
              >
                Therapy Details
              </h3>
              <p
                style={{
                  margin: "6px 0 0",
                  color: Colors.text.sub,
                  fontSize: "13px",
                }}
              >
                Enter the fluid therapy information for this patient.
              </p>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
                gap: "18px",
              }}
            >
              <div>
                <label style={labelStyle}>Fluid Type</label>
                <select
                  name="fluid"
                  value={form.fluid}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="">Select fluid</option>
                  {fluidOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Quantity</label>
                <select
                  name="qty_ml"
                  value={form.qty_ml}
                  onChange={handleChange}
                  style={inputStyle}
                >
                  <option value="">Select quantity</option>
                  {qtyOptions.map((item) => (
                    <option key={item.value} value={item.value}>
                      {item.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={labelStyle}>Speed (ml/hr)</label>
                <input
                  type="number"
                  name="speed_ml_hr"
                  value={form.speed_ml_hr}
                  onChange={handleChange}
                  min="1"
                  style={inputStyle}
                  placeholder="Enter speed"
                />
              </div>

              <div>
                <label style={labelStyle}>Frequency</label>
                <input
                  type="text"
                  name="frequency"
                  value={form.frequency}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Example: 2.5"
                />
              </div>

              <div>
                <label style={labelStyle}>Duration</label>
                <input
                  type="text"
                  name="duration"
                  value={form.duration}
                  onChange={handleChange}
                  style={inputStyle}
                  placeholder="Example: 1 day"
                />
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: "28px",
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              flexWrap: "wrap",
            }}
          >
            <button
              type="button"
              onClick={() => {
                resetForm();
                setErrorMsg("");
                setSuccessMsg("");
              }}
              style={{
                padding: "12px 18px",
                borderRadius: "12px",
                border: `1px solid ${Colors.border.input}`,
                background: Colors.bg.paper,
                color: Colors.text.body,
                cursor: "pointer",
                fontWeight: 700,
                transition: "all 0.2s ease",
              }}
            >
              Reset
            </button>

            <button
              type="submit"
              disabled={saving}
              style={{
                padding: "12px 22px",
                borderRadius: "12px",
                border: "none",
                background: saving
                  ? Colors.text.sub
                  : Colors.brand.primary,
                color: "#fff",
                cursor: saving ? "not-allowed" : "pointer",
                fontWeight: 800,
                minWidth: "170px",
                boxShadow: saving ? "none" : Colors.shadow.brand,
              }}
            >
              {saving ? "Saving..." : "Save Fluid Therapy"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}